import React, { useEffect, useMemo, useRef, useState } from 'react';
import PageContainer from '../../../components/container/PageContainer';
import { COST_HEADER, COST_MODAL_FIELDS } from '../../../constants/cost';
import {
  EmptyCostsGetResponse,
  CostsGetResponse,
  CostItemUI,
  CostListUI,
} from '../../../types/cost/CostGetResponse';
import {
  createCost,
  deleteCost,
  getCostList,
  updateCost,
} from '../../../services/api';
import { Col, Row } from 'react-bootstrap';
import RenderCostRow from './RenderCostRow';
import EditCostRow from './EditCostRow';
import AddCostRow from './AddCostRow';
import SingleAddTable from '../../../components/table/SingleAddTable';
import {
  EmptyCostPostRequest,
  CostPostRequest,
} from '../../../types/cost/CostPostRequest';
import {
  CostPutRequest,
  EmptyCostPutRequest,
  EditModalItem,
} from '../../../types/cost/CostPutRequest';
import { EmptyCostDeleteItem } from '../../../types/cost/CostDeleteItem';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import LoadingOverlay from '../../../components/LoadingOverlay';
import useValidation from '../../../hooks/useValidation';
import { COST_VALIDATION } from '../../../constants/validation/costValidation';
import UpdateModalSave from '../../../components/modal/UpdateModalSave';
import DeleteModal from '../../../components/modal/DeleteModal';
import AddButton from '../../../components/button/AddButton';
import BackToListButton from '../../../components/button/BackToListButton';
import SyncedScrollHeader from '../../../components/layout/SyncedScrollHeader';
import '../../../styles/AssigmentsandCostHeader.css';

const Cost = () => {
  const [editingId, setEditingId] = useState<number>(-1);
  const [isAdding, setIsAdding] = useState(false);
  const [addingItem, setAddingItem] =
    useState<CostPostRequest>(EmptyCostPostRequest);
  const [editingItem, setEditingItem] =
    useState<CostPutRequest>(EmptyCostPutRequest);
  const [deletingItem, setDeletingItem] =
    useState<CostItemUI>(EmptyCostDeleteItem);

  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // テーブル横スクロール（ヘッダー追従用）
  const tableRef = useRef<HTMLDivElement>(null);
  // ヘッダーのref（高さ計算用）
  const headerRef = useRef<HTMLDivElement>(null);
  // フッターのref（高さ計算用）
  const footerRef = useRef<HTMLDivElement>(null);

  // バリデーション関数。エラー時はエラーメッセージを返す。
  const { validate } = useValidation(COST_VALIDATION);

  const queryClient = useQueryClient();

  const { projectId } = useParams<{ projectId: string }>();
  const pid = Number(projectId);

  // post, put delete成功時の処理
  const handleSubmitSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: ['costList', pid],
    });
    setIsAdding(false);
    setAddingItem(EmptyCostPostRequest);
    setEditingId(-1);
  };

  // 一覧取得
  const { data, isLoading, isError, error } = useQuery<
    CostsGetResponse,
    Error,
    CostListUI
  >({
    queryKey: ['costList', pid],
    queryFn: () => getCostList(pid),
    initialData: EmptyCostsGetResponse,
    select: (api) => {
      const categories = api?.categories ?? [];
      const itemsSrc = api?.items ?? [];
      const projects = api?.projects ?? [];

      const categoryMap = new Map(categories.map((c) => [c.id, c.category]));
      const items: CostItemUI[] = itemsSrc.map((prev) => ({
        ...prev,
        category:
          categoryMap.get(prev.costCategoryId) ?? String(prev.costCategoryId),
      }));
      return {
        items,
        categories,
        projects,
        categoryMap,
      };
    },
  });

  // 一覧取得失敗時のアラート
  useEffect(() => {
    if (isError) {
      alert(error as any);
    }
  }, [isError]);

  // 追加
  const postRequest = useMutation({
    mutationFn: (data: CostPostRequest) => createCost(pid, data),
    onSuccess: handleSubmitSuccess,
    onError: (error: any) => alert(error.response.data.message),
  });

  // 削除
  const deleteRequest = useMutation({
    mutationFn: (id: number) => deleteCost(pid, id),
    onSuccess: handleSubmitSuccess,
    onError: (error: any) => alert(error.response.data.message),
  });

  // 編集
  const editRequest = useMutation({
    mutationFn: (data: CostPutRequest) => updateCost(data.id, pid, data),
    onSuccess: handleSubmitSuccess,
    onError: (error: any) => alert(error.response.data.message),
  });

  const onAddButtonClick = () => {
    setIsAdding(true);
    setEditingId(-1);
    setEditingItem(EmptyCostPutRequest);
  };

  const onEditButtonClick = (item: CostItemUI) => {
    setEditingId(item.id);
    setEditingItem({
      id: item.id,
      costCategoryId: item.costCategoryId,
      detail: item.detail,
      date: item.date,
      price: item.price,
    });
    setIsAdding(false);
    setAddingItem(EmptyCostPostRequest);
  };

  const onDeleteButtonClick = (item: CostItemUI) => {
    setDeletingItem(item);
    setShowDeleteModal(true);
  };

  const onEditCancelButtonClick = () => {
    setEditingId(-1);
  };

  const onAddSubmitButtonClick = async () => {
    const validationErrors = validate(addingItem);
    if (validationErrors) {
      alert(validationErrors);
      return;
    }
    postRequest.mutate(addingItem);
  };

  const onEditSubmitButtonClick = async () => {
    setShowUpdateModal(true);
  };

  const onEditModalSubmitButtonClick = async () => {
    const validationErrors = validate(editingItem);
    if (validationErrors) {
      alert(validationErrors);
      return;
    }
    editRequest.mutate({ ...editingItem });
  };

  const onSubmitCancelButtonClick = () => {
    setIsAdding(false);
    setAddingItem(EmptyCostPostRequest);
  };

  const handleAddItemChange = (
    key: keyof CostPostRequest,
    value: string | boolean
  ) => {
    setAddingItem((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleEditItemChange = (
    key: keyof CostPutRequest,
    value: string | boolean
  ) => {
    setEditingItem((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // 表示専用のモーダルアイテム
  const editingModalItem: EditModalItem = useMemo(
    () => ({
      ...editingItem,
      category:
        data.categoryMap.get(editingItem.costCategoryId) ??
        String(editingItem.costCategoryId),
    }),
    [editingItem, data.categoryMap]
  );

  // post, put, deleteが実行中かどうかをチェック
  const isMutating = [postRequest, deleteRequest, editRequest].some(
    (m) => m.status === 'pending'
  );

  return (
    <PageContainer
      title="費用管理"
      subtitle={data.projects[0]?.projectName ?? ''}
    >
      <LoadingOverlay isLoading={isLoading} isMutating={isMutating} />

      <SyncedScrollHeader
        targetRef={tableRef}
        className="mb-3"
        outerRef={headerRef}
      >
        <Row className="header-row-assigmentcost gx-0">
          {/* 左：検索条件（今回はなし。幅は潰さない） */}
          <Col className="header-fields-assigmentcost order-1">
            {/* なし */}
          </Col>

          {/* 右：ボタン群（表右端に追従、必要なら段落ち） */}
          <Col xs="auto" className="header-right-assigmentcost order-2">
            <div className="actions-group-assigmentcost">
              <div className="actions-searchreset-assigmentcost">
                {/* なし */}
              </div>
              <div className="actions-extra-assigmentcost">
                <AddButton onClick={onAddButtonClick} buttonLabel="費用項目" />
              </div>
            </div>
          </Col>
        </Row>
      </SyncedScrollHeader>

      {/* 表本体：横スクロールはここだけ */}
      <SingleAddTable<CostItemUI>
        headers={COST_HEADER}
        items={data.items}
        actionable={true}
        scrollContainerRef={tableRef}
        headerRef={headerRef}
        footerRef={footerRef}
        renderItem={(item: CostItemUI) => (
          <RenderCostRow
            item={item}
            editingId={editingId}
            onEditButtonClick={(it) => onEditButtonClick(it)}
            onDeleteButtonClick={onDeleteButtonClick}
          />
        )}
        editItem={() => (
          <EditCostRow
            item={editingItem}
            categories={data.categories}
            onChange={handleEditItemChange}
            onEditSubmitButtonClick={onEditSubmitButtonClick}
            onEditCancelButtonClick={onEditCancelButtonClick}
          />
        )}
        addItem={() => (
          <AddCostRow
            item={addingItem}
            categories={data.categories}
            onChange={handleAddItemChange}
            onAddSubmitButtonClick={onAddSubmitButtonClick}
            onSubmitCancelButtonClick={onSubmitCancelButtonClick}
          />
        )}
        isAdding={isAdding}
        setIsAdding={setIsAdding}
        editingId={editingId}
      />

      <BackToListButton
        label="プロジェクト一覧に戻る"
        to="/admin/projects"
        ref={footerRef}
      />

      {showUpdateModal && (
        <UpdateModalSave<EditModalItem>
          show={showUpdateModal}
          item={editingModalItem}
          fields={COST_MODAL_FIELDS}
          onClose={() => setShowUpdateModal(false)}
          onSave={() => {
            onEditModalSubmitButtonClick();
            setShowUpdateModal(false);
          }}
        />
      )}
      {showDeleteModal && (
        <DeleteModal<CostItemUI>
          show={showDeleteModal}
          item={deletingItem}
          fields={COST_MODAL_FIELDS}
          onClose={() => setShowDeleteModal(false)}
          onDelete={() => {
            deleteRequest.mutate(deletingItem.id);
            setShowDeleteModal(false);
          }}
        />
      )}
    </PageContainer>
  );
};

export default Cost;
